/**
 * Audit log API client.
 *
 * Both audit pages used to read Firestore directly and both were broken: the
 * tenant page never called anything at all (a `setLogs([])` stub left behind a
 * TODO whose backend had shipped months earlier), and the admin page queried a
 * `auditLogs` collection that only the client-side org switcher ever wrote to,
 * while every server-side writer uses `audit_logs`. Everything now goes through
 * the REST API, which reads the real collection and applies the RBAC the pages
 * were only assuming.
 */

import { apiRequest } from './api';
import type { AuditLog } from '@simcheck/shared-types';

export interface AuditLogFilters {
  /** Restrict to actions by one user. */
  actorId?: string;
  /** Restrict to one audit action. */
  action?: string;
  /** Restrict to one resource type. */
  resourceType?: string;
  /** Maximum rows. The API defaults to 100. */
  limit?: number;
  /** ISO 8601 lower bound. */
  startDate?: string;
  /** ISO 8601 upper bound. */
  endDate?: string;
  /** Device-scoped entries only: restrict to one entry kind. */
  kind?: string;
}

interface AuditLogListResponse {
  logs: AuditLog[];
  count: number;
}

export interface AuditStats {
  totalActions: number;
  actionsByType: Record<string, number>;
  actionsByUser: Record<string, number>;
  failedActions: number;
  recentActivity: AuditLog[];
}

function toQueryString(filters: AuditLogFilters = {}): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

/**
 * Timestamps arrive as ISO strings over JSON; the UI wants Date objects.
 * Anything unparseable becomes the epoch rather than an Invalid Date that
 * would render as "NaN" halfway down a table.
 */
function reviveLog(log: any): AuditLog {
  const parsed = log?.timestamp ? new Date(log.timestamp) : null;
  return {
    ...log,
    timestamp: parsed && !Number.isNaN(parsed.getTime()) ? parsed : new Date(0),
  } as AuditLog;
}

/** One organization's audit log. Requires a system role, or owner/admin of that org. */
export async function getOrganizationAuditLogs(
  organizationId: string,
  filters: AuditLogFilters = {},
): Promise<AuditLog[]> {
  const res = await apiRequest<AuditLogListResponse>(
    `/audit-logs/organization/${encodeURIComponent(organizationId)}${toQueryString(filters)}`,
    {},
    organizationId,
  );
  return (res.logs ?? []).map(reviveLog);
}

/** Platform-wide audit log. Super admin and support only. */
export async function getSystemAuditLogs(filters: AuditLogFilters = {}): Promise<AuditLog[]> {
  const res = await apiRequest<AuditLogListResponse>(
    `/audit-logs/system${toQueryString(filters)}`,
  );
  return (res.logs ?? []).map(reviveLog);
}

/** Everything that touched one resource (e.g. `flow/123`), newest first. */
export async function getResourceAuditHistory(
  resource: string,
  limit = 50,
): Promise<AuditLog[]> {
  const res = await apiRequest<AuditLogListResponse>(
    `/audit-logs/resource/${encodeURIComponent(resource)}?limit=${limit}`,
  );
  return (res.logs ?? []).map(reviveLog);
}

/**
 * Device-scoped audit entries (AT-policy decisions and similar). Shape varies
 * by writer, so these come back raw rather than as `AuditLog`.
 */
export async function getDeviceAuditLogs(
  deviceId: string,
  kind?: string,
  limit = 100,
): Promise<Array<Record<string, any>>> {
  const qs = toQueryString({ ...(kind ? { kind } : {}), limit } as AuditLogFilters);
  const res = await apiRequest<{ logs: Array<Record<string, any>> }>(
    `/audit-logs/device/${encodeURIComponent(deviceId)}${qs}`,
  );
  return res.logs ?? [];
}

/** Aggregate counts for one organization. Same permissions as the org log. */
export async function getAuditStats(
  organizationId: string,
  filters: Pick<AuditLogFilters, 'startDate' | 'endDate'> = {},
): Promise<AuditStats> {
  const res = await apiRequest<{ stats: AuditStats }>(
    `/audit-logs/stats/${encodeURIComponent(organizationId)}${toQueryString(filters)}`,
    {},
    organizationId,
  );
  return res.stats;
}
